package com.mgacreative.hotel.domain.repository

import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import com.mgacreative.hotel.domain.model.CashMovement
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.Serializable

@Serializable
data class CurrentAccount(
    val id: String = "",
    val tenant_id: String = "",
    val name: String,
    val type: String, // SUPPLIER, CUSTOMER
    val balance: Double = 0.0
)

interface FinanceRepository {
    fun observeCashMovements(): Flow<List<CashMovement>>
    fun observeCurrentAccounts(): Flow<List<CurrentAccount>>
    suspend fun loadCashMovements()
    suspend fun loadCurrentAccounts()
    suspend fun addCashMovement(type: String, amount: Double, description: String, date: String)
    suspend fun addCurrentAccount(name: String, type: String, initialBalance: Double)
}

class FinanceRepositoryImpl(
    private val networkClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession
) : FinanceRepository {

    private val _movements = MutableStateFlow<List<CashMovement>>(emptyList())
    private val _accounts = MutableStateFlow<List<CurrentAccount>>(emptyList())

    override fun observeCashMovements(): Flow<List<CashMovement>> = _movements.asStateFlow()
    override fun observeCurrentAccounts(): Flow<List<CurrentAccount>> = _accounts.asStateFlow()

    override suspend fun loadCashMovements() {
        if (tenantSession.isDemoMode) {
            _movements.value = com.mgacreative.hotel.domain.demo.DemoDataProvider.cashMovementsState.value
            return
        }
        try {
            val tenantId = tenantSession.currentTenantId
            if (tenantId != null) {
                val result = networkClient.client.postgrest["cash_movements"]
                    .select {
                        filter { eq("tenant_id", tenantId) }
                    }
                    .decodeList<CashMovement>()
                    
                _movements.value = result.sortedByDescending { it.created_at }
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun loadCurrentAccounts() {
        if (tenantSession.isDemoMode) {
            _accounts.value = com.mgacreative.hotel.domain.demo.DemoDataProvider.currentAccountsState.value
            return
        }
        try {
            val tenantId = tenantSession.currentTenantId
            if (tenantId != null) {
                val result = networkClient.client.postgrest["current_accounts"]
                    .select {
                        filter { eq("tenant_id", tenantId) }
                    }
                    .decodeList<CurrentAccount>()
                    
                _accounts.value = result
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun addCashMovement(type: String, amount: Double, description: String, date: String) {
        if (tenantSession.isDemoMode) {
            val newMovement = CashMovement(
                id = "cm-${(100..999).random()}",
                tenant_id = com.mgacreative.hotel.domain.demo.DemoDataProvider.DEMO_TENANT_ID,
                type = type,
                amount = amount,
                description = description,
                movement_date = date,
                created_at = date
            )
            com.mgacreative.hotel.domain.demo.DemoDataProvider.cashMovementsState.value = listOf(newMovement) + com.mgacreative.hotel.domain.demo.DemoDataProvider.cashMovementsState.value
            _movements.value = com.mgacreative.hotel.domain.demo.DemoDataProvider.cashMovementsState.value
            return
        }
        try {
            val tenantId = tenantSession.currentTenantId
            if (tenantId != null) {
                @kotlinx.serialization.Serializable
                data class MovementRequest(
                    val tenant_id: String,
                    val type: String,
                    val amount: Double,
                    val description: String,
                    val movement_date: String
                )
                
                val req = MovementRequest(tenantId, type, amount, description, date)
                networkClient.client.postgrest["cash_movements"].insert(req)
                
                // Refresh list
                loadCashMovements()
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun addCurrentAccount(name: String, type: String, initialBalance: Double) {
        if (tenantSession.isDemoMode) {
            val newAccount = CurrentAccount(
                id = "ca-${(100..999).random()}",
                tenant_id = com.mgacreative.hotel.domain.demo.DemoDataProvider.DEMO_TENANT_ID,
                name = name,
                type = type,
                balance = initialBalance
            )
            com.mgacreative.hotel.domain.demo.DemoDataProvider.currentAccountsState.value = com.mgacreative.hotel.domain.demo.DemoDataProvider.currentAccountsState.value + newAccount
            _accounts.value = com.mgacreative.hotel.domain.demo.DemoDataProvider.currentAccountsState.value
            return
        }
        try {
            val tenantId = tenantSession.currentTenantId
            if (tenantId != null) {
                @kotlinx.serialization.Serializable
                data class AccountRequest(
                    val tenant_id: String,
                    val name: String,
                    val type: String,
                    val balance: Double
                )
                
                val req = AccountRequest(tenantId, name, type, initialBalance)
                networkClient.client.postgrest["current_accounts"].insert(req)
                
                // Refresh list
                loadCurrentAccounts()
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }
}

